<template>
  <h3 class="secondary-title"><span>Sample Media / </span> {{ lensName }}</h3>
  <div class="sample-image-row">
    <div
      class="sample-img-col"
      v-for="(src, index) in props.images"
      :key="index"
      @click="openLightbox(index)"
    >
      <div class="media-wrapper" oncontextmenu="return false;">
        <template v-if="isImage(src)">
          <img :src="src" class="media" />
        </template>
        <template v-else>
          <video
            :src="src + '#t=0.1'"
            preload="metadata"
            muted
            playsinline
            class="media thumb-video"
          />
          <div class="thumb-play-icon">
            <img src="../assets/img/play.png" />
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Unified lightbox carousel -->
  <div v-if="lightboxVisible" class="lightbox-overlay" @click.self="closeLightbox">
    <img src="../assets/img/cross-icon.svg" class="lb-close" @click="closeLightbox" />

    <button class="lb-nav lb-prev" @click="navigate(-1)" v-if="props.images.length > 1">
      &#8249;
    </button>

    <div class="lb-media">
      <template v-if="isImage(props.images[currentIndex])">
        <img
          :src="props.images[currentIndex]"
          class="lb-img"
          oncontextmenu="return false;"
          @dragstart.prevent
        />
      </template>
      <template v-else>
        <div class="lb-video-box" @click="togglePlayPause">
          <video
            :key="currentIndex"
            ref="lightboxVideoRef"
            disablepictureinpicture
            oncontextmenu="return false;"
            controlslist="nodownload noremoteplayback noplaybackrate"
            @play="isVideoPlaying = true"
            @pause="isVideoPlaying = false"
            @ended="isVideoPlaying = false"
            playsinline
          >
            <source :src="props.images[currentIndex]" type="video/mp4" />
          </video>
          <div class="play-button-img" v-if="!isVideoPlaying" @click.stop="togglePlayPause">
            <img src="@/assets/img/play.png" />
          </div>
        </div>
      </template>
    </div>

    <button class="lb-nav lb-next" @click="navigate(1)" v-if="props.images.length > 1">
      &#8250;
    </button>

    <div class="lb-counter">{{ currentIndex + 1 }} / {{ props.images.length }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps } from 'vue'

const props = defineProps(['images', 'lensName'])

const lightboxVisible = ref(false)
const currentIndex = ref(0)
const lightboxVideoRef = ref(null)
const isVideoPlaying = ref(false)

const isImage = src => !src.includes('.mp4')

const openLightbox = index => {
  currentIndex.value = index
  isVideoPlaying.value = false
  lightboxVisible.value = true
}

const closeLightbox = () => {
  if (lightboxVideoRef.value) lightboxVideoRef.value.pause()
  isVideoPlaying.value = false
  lightboxVisible.value = false
}

const navigate = dir => {
  if (lightboxVideoRef.value) lightboxVideoRef.value.pause()
  isVideoPlaying.value = false
  const len = props.images.length
  currentIndex.value = (currentIndex.value + dir + len) % len
}

const togglePlayPause = () => {
  const video = lightboxVideoRef.value
  if (!video) return
  if (video.paused) {
    video.play()
  } else {
    video.pause()
  }
}

function handleKeydown(e) {
  if (!lightboxVisible.value) return
  if (e.key === 'Escape') closeLightbox()
  if (e.key === 'ArrowRight') navigate(1)
  if (e.key === 'ArrowLeft') navigate(-1)
  if (e.key === ' ') {
    e.preventDefault()
    if (!isImage(props.images[currentIndex.value])) togglePlayPause()
  }
}

onMounted(() => {
  document.querySelectorAll('img').forEach(img => {
    img.addEventListener('dragstart', e => e.preventDefault())
  })
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.secondary-title {
  font-size: 12px;
}
.secondary-title span {
  color: rgb(120, 120, 120);
}

/* ── Grid ── */
.sample-image-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
  align-items: center;
}

.sample-img-col {
  flex-basis: calc((100% - 20px) / 3);
  cursor: pointer;
  position: relative;
  border: 1px solid grey;
}

@media screen and (max-width: 740px) {
  .sample-img-col {
    flex-basis: 100%;
  }
}

.media-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
}

.media {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-placeholder {
  background: #1a1e1e;
}

.thumb-video {
  pointer-events: none;
}

.thumb-play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.thumb-play-icon img {
  width: 16px;
  height: 16px;
}

/* ── Lightbox ── */
.lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.lb-close {
  position: absolute;
  top: 28px;
  right: 28px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  z-index: 1002;
  transition: scale 0.15s;
}
.lb-close:hover {
  scale: 1.15;
}

.lb-media {
  max-width: 88vw;
  max-height: 88vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lb-img {
  max-width: 100%;
  max-height: 88vh;
  object-fit: contain;
  display: block;
  user-select: none;
}

/* Video inside lightbox — same box pattern as selects/browse */
.lb-video-box {
  position: relative;
  cursor: pointer;
  max-width: 88vw;
}

.lb-video-box video {
  display: block;
  max-width: 88vw;
  max-height: 82vh;
}

.play-button-img {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.play-button-img img {
  width: 55px;
}

/* Nav arrows — pinned to screen edges */
.lb-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: white;
  font-size: 52px;
  line-height: 1;
  cursor: pointer;
  padding: 16px 20px;
  opacity: 0.6;
  transition: opacity 0.15s;
  user-select: none;
  z-index: 1001;
}
.lb-prev {
  left: 0;
}
.lb-next {
  right: 0;
}
.lb-nav:hover {
  opacity: 1;
}

.lb-counter {
  position: absolute;
  bottom: 22px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.55);
  font-size: 11px;
  letter-spacing: 1.5px;
}
</style>
